import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { axiosInstance, API } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Store } from "lucide-react";

const Counter = ({ user }) => {
  const navigate = useNavigate();
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCounter, setCurrentCounter] = useState(null);
  
  const [formData, setFormData] = useState({
    nama_counter: "",
    lokasi: "",
    tipe_harga: "default",
  });

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchCounters();
  }, [user, navigate]);

  const fetchCounters = async () => {
    try {
      const response = await axiosInstance.get(`${API}/counters`);
      setCounters(response.data);
    } catch (error) {
      toast.error("Gagal memuat counter");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editMode) {
        await axiosInstance.put(`${API}/counters/${currentCounter.id}`, formData);
        toast.success("Counter berhasil diperbarui");
      } else {
        await axiosInstance.post(`${API}/counters`, formData);
        toast.success("Counter berhasil ditambahkan");
      }

      setDialogOpen(false);
      resetForm();
      fetchCounters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operasi gagal");
    }
  };

  const handleEdit = (counter) => {
    setCurrentCounter(counter);
    setFormData({
      nama_counter: counter.nama_counter,
      lokasi: counter.lokasi,
      tipe_harga: counter.tipe_harga || "default",
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus counter ini?")) return;

    try {
      await axiosInstance.delete(`${API}/counters/${id}`);
      toast.success("Counter berhasil dihapus");
      fetchCounters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hapus gagal");
    }
  };

  const resetForm = () => {
    setFormData({
      nama_counter: "",
      lokasi: "",
      tipe_harga: "default",
    });
    setEditMode(false);
    setCurrentCounter(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/">
                <Button variant="ghost" size="sm" data-testid="back-button">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src="/logo-dumont.jpg" alt="Dumont" className="h-12 w-auto" />
              <div className="border-l border-gray-300 pl-3">
                <h1 className="text-2xl font-bold text-gray-800">Master Counter</h1>
                <p className="text-sm text-gray-600">Kelola lokasi counter</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">Daftar Counter</CardTitle>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-counter-button"
                    onClick={resetForm}
                    className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah Counter
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="counter-dialog">
                  <DialogHeader>
                    <DialogTitle>{editMode ? "Edit Counter" : "Tambah Counter Baru"}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="nama_counter">Nama Counter</Label>
                      <Input
                        id="nama_counter"
                        data-testid="counter-nama-input"
                        value={formData.nama_counter}
                        onChange={(e) => setFormData({ ...formData, nama_counter: e.target.value })}
                        placeholder="Contoh: DEPO BAGOES"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lokasi">Lokasi</Label>
                      <Input
                        id="lokasi"
                        data-testid="counter-lokasi-input"
                        value={formData.lokasi}
                        onChange={(e) => setFormData({ ...formData, lokasi: e.target.value })}
                        placeholder="Contoh: Jl. Raya No. 123"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tipe_harga">Tipe Harga</Label>
                      <Select
                        value={formData.tipe_harga}
                        onValueChange={(value) => setFormData({ ...formData, tipe_harga: value })}
                      >
                        <SelectTrigger id="tipe_harga" data-testid="counter-tipe-harga-select">
                          <SelectValue placeholder="Pilih tipe harga" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Default (Harga Normal)</SelectItem>
                          <SelectItem value="khusus">Khusus (Harga Spesial)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        Pilih "Khusus" untuk counter yang mendapat harga khusus
                      </p>
                    </div>

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        data-testid="cancel-button"
                      >
                        Batal
                      </Button>
                      <Button
                        type="submit"
                        data-testid="submit-counter-button"
                        className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                      >
                        {editMode ? "Perbarui" : "Simpan"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-600">Memuat...</div>
            ) : counters.length === 0 ? (
              <div className="text-center py-8 text-gray-600">Belum ada counter</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nama Counter</TableHead>
                      <TableHead>Lokasi</TableHead>
                      <TableHead>Tipe Harga</TableHead>
                      <TableHead className="text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {counters.map((counter) => (
                      <TableRow key={counter.id}>
                        <TableCell className="font-medium">{counter.nama_counter}</TableCell>
                        <TableCell>{counter.lokasi}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            counter.tipe_harga === 'khusus' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {counter.tipe_harga === 'khusus' ? 'Harga Khusus' : 'Harga Default'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(counter)}
                              data-testid={`edit-counter-${counter.id}`}
                            >
                              <Pencil className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(counter.id)}
                              data-testid={`delete-counter-${counter.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Counter;
